import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertCircle, X } from "lucide-react";
import { formatINR } from "../../lib/utils-cynos";
import { useCoupon } from "../../context/CouponContext";

const CartSummary = ({ items = [], subtotal, discount, total, disabled, onCheckoutClick }) => {
  const {
    appliedCoupon,
    discountAmount,
    loading,
    error,
    message,
    applyCoupon,
    removeCoupon,
  } = useCoupon();
  const [couponInput, setCouponInput] = useState("");
  const payableTotal = Math.max(0, total - discountAmount);

  useEffect(() => {
    if (appliedCoupon?.code) setCouponInput(appliedCoupon.code);
  }, [appliedCoupon]);

  useEffect(() => {
    if (disabled && appliedCoupon) {
      removeCoupon(true);
      setCouponInput("");
    }
  }, [appliedCoupon, disabled, removeCoupon]);

  const handleApply = async () => {
    if (!couponInput.trim()) return;
    await applyCoupon(couponInput, subtotal, null, items);
  };

  const handleRemove = () => {
    removeCoupon();
    setCouponInput("");
  };

  return (
    <div className="border border-white/10 bg-cynos-card p-6 space-y-3">
      <div className="flex justify-between font-mono text-xs uppercase tracking-[0.2em] text-zinc-400">
        <span>Subtotal</span><span>{formatINR(subtotal)}</span>
      </div>
      {discount > 0 && (
        <div className="flex justify-between font-mono text-xs uppercase tracking-[0.2em] text-cynos-red">
          <span>Buy 2 Get 1 Free</span><span>-{formatINR(discount)}</span>
        </div>
      )}

      <div className="border-t border-white/10 pt-4">
        {!appliedCoupon ? (
          <div className="flex gap-2">
            <input
              value={couponInput}
              onChange={(event) => setCouponInput(event.target.value.toUpperCase())}
              onKeyDown={(event) => {
                if (event.key === "Enter") handleApply();
              }}
              placeholder="DISCOUNT CODE"
              disabled={loading || disabled}
              className="min-w-0 flex-1 bg-cynos-surface border border-white/15 px-3 py-3 font-mono text-[10px] uppercase tracking-[0.18em] outline-none focus:border-white disabled:opacity-50"
              data-testid="cart-coupon-input"
            />
            <button
              type="button"
              onClick={handleApply}
              disabled={loading || disabled || !couponInput.trim()}
              className="border border-white/30 px-3 font-mono text-[10px] uppercase tracking-[0.18em] hover:bg-white/10 disabled:opacity-50"
              data-testid="cart-coupon-apply"
            >
              {loading ? "..." : "Apply"}
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between border border-emerald-500/30 bg-emerald-500/10 px-3 py-3">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-emerald-300">{appliedCoupon.code}</div>
              <div className="mt-1 font-mono text-[9px] uppercase tracking-[0.16em] text-emerald-200">Coupon Applied</div>
            </div>
            <button type="button" onClick={handleRemove} className="text-emerald-200 hover:text-white" aria-label="Remove coupon">
              <X size={15} />
            </button>
          </div>
        )}

        {error && (
          <div className="mt-2 flex gap-2 border border-red-500/30 bg-red-500/10 p-2 font-mono text-[10px] uppercase tracking-[0.14em] text-red-200">
            <AlertCircle size={14} className="mt-0.5 shrink-0" /> {error}
          </div>
        )}
        {message && appliedCoupon && (
          <div className="mt-2 border border-emerald-500/30 bg-emerald-500/10 p-2 font-mono text-[10px] uppercase tracking-[0.14em] text-emerald-200">
            {message}
          </div>
        )}
      </div>

      {discountAmount > 0 && (
        <div className="flex justify-between font-mono text-xs uppercase tracking-[0.2em] text-cynos-red">
          <span>Coupon Discount</span><span>-{formatINR(discountAmount)}</span>
        </div>
      )}
      <div className="flex justify-between items-baseline border-t border-white/10 pt-4">
        <span className="font-mono text-xs uppercase tracking-[0.2em]">Total</span>
        <span className="font-anton text-2xl">{formatINR(payableTotal)}</span>
      </div>
      <Link
        to="/checkout"
        onClick={onCheckoutClick}
        className={`block text-center w-full bg-cynos-red text-white py-4 font-mono text-xs uppercase tracking-[0.2em] hover:bg-red-600 transition-colors ${disabled ? "pointer-events-none opacity-40" : ""}`}
        data-testid="cart-checkout-btn"
      >
        Checkout
      </Link>
    </div>
  );
};

export default CartSummary;
